import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  ArrowLeft,
  Save,
  AlertCircle,
  Sparkles,
  Swords,
  Gamepad2,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getLeague } from "../../graphql/queries";
import { updateLeague } from "../../graphql/mutations";
import { formatDateForInput } from "../../utils/dateUtils";
import {
  DEFAULT_LEAGUE_GAME,
  LEAGUE_GAMES,
  LeagueGame,
  getLeagueFormats,
  getLeagueGame,
} from "../../lib/leagueGames";
import LeagueScoringFields from "../../components/LeagueScoringFields";
import {
  DEFAULT_LEAGUE_SCORING,
  getLeagueScoring,
  LeagueScoringConfig,
} from "../../lib/scoring";

const client = generateClient();

interface LeagueFormData extends LeagueScoringConfig {
  name: string;
  description: string;
  game: LeagueGame;
  format: string;
  maxParticipants: number | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const EditLeague: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<LeagueFormData>({
    name: "",
    description: "",
    game: DEFAULT_LEAGUE_GAME,
    format: "Standard",
    maxParticipants: null,
    startDate: "",
    endDate: "",
    isActive: true,
    ...DEFAULT_LEAGUE_SCORING,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch league data
  const {
    data: league,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["league", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getLeague,
        variables: { id: leagueId! },
      });
      return result.data.getLeague;
    },
    enabled: !!leagueId,
  });

  // Update form data when league is loaded
  useEffect(() => {
    if (league) {
      setFormData({
        name: league.name || "",
        description: league.description || "",
        game: getLeagueGame(league.game),
        format: league.format || "",
        maxParticipants: league.maxParticipants || null,
        startDate: league.startDate ? formatDateForInput(league.startDate) : "",
        endDate: league.endDate ? formatDateForInput(league.endDate) : "",
        isActive: league.isActive ?? true,
        ...getLeagueScoring(league),
      });
    }
  }, [league]);

  // Update league mutation
  const updateLeagueMutation = useMutation({
    mutationFn: async (updatedData: LeagueFormData) => {
      const result = await client.graphql({
        query: updateLeague,
        variables: {
          input: {
            id: leagueId!,
            ...updatedData,
            // Convert empty string dates to null
            startDate: updatedData.startDate || null,
            endDate: updatedData.endDate || null,
            // Convert empty maxParticipants to null
            maxParticipants: updatedData.maxParticipants || null,
          },
        },
      });
      return result.data.updateLeague;
    },
    onSuccess: () => {
      // Invalidate and refetch league data
      queryClient.invalidateQueries({ queryKey: ["league", leagueId] });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });
      navigate(`/admin/leagues/${leagueId}`);
    },
    onError: (error: any) => {
      console.error("Error updating league:", error);
      setErrors({ submit: "Failed to update league. Please try again." });
    },
  });

  const handleInputChange = (
    field: keyof LeagueFormData,
    value: string | number | boolean | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGameChange = (game: LeagueGame) => {
    setFormData((prev) => ({
      ...prev,
      game,
      format: getLeagueFormats(game)[0],
    }));
  };

  const handleScoringChange = (
    field: keyof LeagueScoringConfig,
    value: number
  ) => {
    handleInputChange(field, value);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "League name is required";
    }

    if (!formData.format.trim()) {
      newErrors.format = "Format is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (
      formData.endDate &&
      formData.startDate &&
      formData.endDate < formData.startDate
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    if (formData.maxParticipants !== null && formData.maxParticipants <= 0) {
      newErrors.maxParticipants = "Max participants must be greater than 0";
    }

    Object.entries(getLeagueScoring(formData)).forEach(([field, value]) => {
      const minimum = field === "premiumMultiplier" ? 1 : 0;
      if (!Number.isInteger(value) || value < minimum) {
        newErrors[field] = `Must be a whole number of at least ${minimum}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    updateLeagueMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading league: {error.message}
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">League not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl p-0 sm:p-2">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to League
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Edit League</h1>
          <p className="text-muted-foreground mt-2">
            Update league information and settings
          </p>
        </div>
      </div>

      <Card className="overflow-hidden border-white/10 bg-card/80 shadow-2xl shadow-black/20 backdrop-blur-xl">
        <CardHeader>
          <CardTitle>League Details</CardTitle>
          <CardDescription>Update the league information below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Game *</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
                  const option = LEAGUE_GAMES[game];
                  const Icon =
                    game === "MAGIC_THE_GATHERING"
                      ? Sparkles
                      : game === "RIFTBOUND"
                      ? Swords
                      : Gamepad2;
                  const selected = formData.game === game;

                  return (
                    <button
                      key={game}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => handleGameChange(game)}
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        selected
                          ? "border-primary bg-primary/15 shadow-lg shadow-primary/10"
                          : "border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]"
                      }`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            selected
                              ? "bg-primary text-white"
                              : "bg-white/[0.06] text-muted-foreground"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </span>
                        <span
                          className={`h-3 w-3 rounded-full border ${
                            selected
                              ? "border-primary bg-primary ring-4 ring-primary/20"
                              : "border-white/30"
                          }`}
                        />
                      </div>
                      <span className="block font-semibold">
                        {option.label}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* League Name */}
            <div className="space-y-2">
              <Label htmlFor="name">League Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Enter league name"
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter league description"
                rows={3}
              />
            </div>

            {/* Format */}
            <div className="space-y-2">
              <Label htmlFor="format">Format *</Label>
              <Select
                value={formData.format}
                onValueChange={(value) => handleInputChange("format", value)}
              >
                <SelectTrigger
                  className={errors.format ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  {getLeagueFormats(formData.game).map((format) => (
                    <SelectItem key={format} value={format}>
                      {format}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.format && (
                <p className="text-sm text-red-600">{errors.format}</p>
              )}
            </div>

            {/* Max Participants */}
            <div className="space-y-2">
              <Label htmlFor="maxParticipants">Max Participants</Label>
              <Input
                id="maxParticipants"
                type="number"
                min="1"
                value={formData.maxParticipants || ""}
                onChange={(e) =>
                  handleInputChange(
                    "maxParticipants",
                    e.target.value ? parseInt(e.target.value) : null
                  )
                }
                placeholder="Leave empty for unlimited"
                className={errors.maxParticipants ? "border-red-500" : ""}
              />
              {errors.maxParticipants && (
                <p className="text-sm text-red-600">{errors.maxParticipants}</p>
              )}
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                className={errors.startDate ? "border-red-500" : ""}
              />
              {errors.startDate && (
                <p className="text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange("endDate", e.target.value)}
                className={errors.endDate ? "border-red-500" : ""}
              />
              {errors.endDate && (
                <p className="text-sm text-red-600">{errors.endDate}</p>
              )}
            </div>

            {/* Active Status */}
            <div className="space-y-2">
              <Label htmlFor="isActive">Status</Label>
              <Select
                value={formData.isActive.toString()}
                onValueChange={(value) =>
                  handleInputChange("isActive", value === "true")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <LeagueScoringFields
              value={formData}
              onChange={handleScoringChange}
              isCommander={formData.format === "Commander"}
            />

            {/* Submit Error */}
            {errors.submit && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={updateLeagueMutation.isPending}
                className="flex-1"
              >
                {updateLeagueMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/admin/leagues/${leagueId}`)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditLeague;
